import { dayjsSerializer } from "components/manualReview/utils/timeUtils";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import { z } from "zod";
import { FrozenGroup } from "./targetedReviewRows";

dayjs.extend(utc);

const selectionSchema = z.object({
  start: z.iso.datetime(),
  end: z.iso.datetime(),
  rowIds: z.array(z.iso.datetime()).min(1),
});

const parseSelection = ({
  period,
  day,
}: {
  period: string | null;
  day: Dayjs;
}): FrozenGroup | undefined => {
  if (!period) return undefined;
  try {
    const parsed = selectionSchema.safeParse(JSON.parse(period));
    if (!parsed.success) return undefined;
    const start = dayjs.utc(parsed.data.start);
    const end = dayjs.utc(parsed.data.end);
    if (!start.isSame(day, "day") || !end.isAfter(start)) return undefined;
    return { start, end, rowIds: parsed.data.rowIds };
  } catch {
    return undefined;
  }
};

export const useTargetedReviewSelection = ({
  location,
  day,
  canOpen,
}: {
  location: string | undefined;
  day: Dayjs;
  canOpen: (group: FrozenGroup) => boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get("period");
  const requestedSelection = useMemo(
    () => parseSelection({ period, day }),
    [period, day]
  );

  const openedPeriodRef = useRef<string | null>(null);
  const pendingPeriodRef = useRef<string | null | undefined>(undefined);
  if (pendingPeriodRef.current !== undefined) {
    // Router updates land in a transition, so a render can still see the URL
    // from before our own write; keep showing what was open until it catches up.
    if (period === pendingPeriodRef.current) {
      openedPeriodRef.current = period;
      pendingPeriodRef.current = undefined;
    }
  } else if (
    requestedSelection &&
    period !== openedPeriodRef.current &&
    canOpen(requestedSelection)
  ) {
    openedPeriodRef.current = period;
  }
  const selection =
    period !== null && period === openedPeriodRef.current
      ? requestedSelection
      : undefined;

  const latestRef = useRef({ setSearchParams, day });
  latestRef.current = { setSearchParams, day };

  const setSelection = useCallback(
    ({
      group,
      history,
    }: {
      group: FrozenGroup | undefined;
      history: "push" | "replace";
    }) => {
      const nextPeriod = group ? JSON.stringify(group) : null;
      pendingPeriodRef.current = nextPeriod;
      const { setSearchParams: setLatestSearchParams, day: latestDay } =
        latestRef.current;
      setLatestSearchParams(
        (current) => {
          const nextParams = new URLSearchParams(current);
          if (!nextParams.has("date")) {
            nextParams.set("date", dayjsSerializer.format(latestDay));
          }
          if (nextPeriod === null) {
            nextParams.delete("period");
          } else {
            nextParams.set("period", nextPeriod);
          }
          return nextParams;
        },
        { replace: history === "replace" }
      );
    },
    []
  );

  const dayKey = day.valueOf();
  const previousContextRef = useRef({ location, dayKey });
  useEffect(() => {
    const previous = previousContextRef.current;
    if (previous.location === location && previous.dayKey === dayKey) return;
    previousContextRef.current = { location, dayKey };
    setSelection({ group: undefined, history: "replace" });
  }, [location, dayKey, setSelection]);

  return [selection, setSelection] as const;
};
